import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementNode,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';

export type SerializedLayoutContainerNode = Spread<
  {
    templateColumns: string;
  },
  SerializedElementNode
>;

function convertLayoutContainerElement(
  domNode: HTMLElement
): DOMConversionOutput | null {
  const styleAttribute = domNode.style.gridTemplateColumns;
  if (styleAttribute) {
    const node = $createLayoutContainerNode(styleAttribute);
    return { node };
  }
  return null;
}

export class LayoutContainerNode extends ElementNode {
  __templateColumns: string;

  static getType(): string {
    return 'layout-container';
  }

  static clone(node: LayoutContainerNode): LayoutContainerNode {
    return new LayoutContainerNode(node.__templateColumns, node.__key);
  }

  constructor(templateColumns: string, key?: NodeKey) {
    super(key);
    this.__templateColumns = templateColumns;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = document.createElement('div');
    dom.style.display = 'grid';
    dom.style.gap = '10px';
    dom.style.gridTemplateColumns = this.__templateColumns;
    dom.className = 'layout-container';
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.style.display = 'grid';
    element.style.gap = '10px';
    element.style.gridTemplateColumns = this.__templateColumns;
    element.setAttribute('data-lexical-layout-container', 'true');
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (domNode.hasAttribute('data-lexical-layout-container')) {
          return {
            conversion: convertLayoutContainerElement,
            priority: 2,
          };
        }
        return null;
      },
    };
  }

  isShadowRoot(): boolean {
    return true;
  }

  updateDOM(): boolean {
    return false;
  }

  static importJSON(
    serializedNode: SerializedLayoutContainerNode
  ): LayoutContainerNode {
    const { templateColumns } = serializedNode;
    return $createLayoutContainerNode(templateColumns);
  }

  exportJSON(): SerializedLayoutContainerNode {
    return {
      ...super.exportJSON(),
      templateColumns: this.__templateColumns,
      type: 'layout-container',
      version: 1,
    };
  }

  getTemplateColumns(): string {
    return this.__templateColumns;
  }

  setTemplateColumns(templateColumns: string): void {
    const writable = this.getWritable();
    writable.__templateColumns = templateColumns;
  }
}

export function $createLayoutContainerNode(
  templateColumns: string
): LayoutContainerNode {
  return new LayoutContainerNode(templateColumns);
}

export function $isLayoutContainerNode(
  node: LexicalNode | null | undefined
): node is LayoutContainerNode {
  return node instanceof LayoutContainerNode;
}
